import Vue from "vue";
import { Tolgee, DevTools, FormatSimple } from "@tolgee/web";
import { InContextTools } from "@tolgee/web/tools";
import cookies from "@/utils/cookie";
import elementLocale from "element-ui/lib/locale";
import { Locale as vantLocale } from "vant";

// 引入各个语言配置文件
import en from "./en.json"; // 英语
import zh from "./zh.json"; // 简体中文
import tw from "./tw.json"; // 繁体中文
import es from "./es.json"; // 西班牙语
import fr from "./fr.json"; // 法语
import id from "./id.json"; // 印度语
import th from "./th.json"; // 泰语
import ar from "./ar.json"; // 阿拉伯语
import pt from "./pt.json"; // 葡萄牙语
import ru from "./ru.json"; // 俄语
import uz from "./uz.json"; // 乌兹别克语
import am from "./am.json"; // 阿美尼亚语
import it from "./it.json"; // 意大利语

// 语言映射表
const elementUIMap = {
  zh: "zh-CN",
  tw: "zh-TW",
  es: "es",
  en: "en",
  fr: "fr",
  id: "id",
  th: "th",
  ar: "ar",
  pt: "pt",
  ru: "ru-RU",
  uz: "uz-UZ",
  am: "hy-AM",
  it: "it",
};

const vantUIMap = {
  zh: "zh-CN",
  tw: "zh-TW",
  es: "es-ES",
  en: "en-US",
  fr: "fr-FR",
  id: "en-US",
  th: "th-TH",
  ar: "en-US",
  pt: "en-US",
  ru: "en-US",
  uz: "en-US",
  am: "en-US",
  it: "en-US",
};

const messages = { en, zh, tw, es, fr, id, th, ar, pt, ru, uz, am, it };

const supportedLocales = Object.keys(messages);

const localeAliases = {
  "zh-cn": "zh",
  "zh-hans": "zh",
  "zh-hans-cn": "zh",
  "zh-sg": "zh",
  "zh-hant": "tw",
  "zh-hant-tw": "tw",
  "zh-tw": "tw",
  "zh-hk": "tw",
  "en-us": "en",
  "en-gb": "en",
  "es-es": "es",
  "fr-fr": "fr",
  "id-id": "id",
  "th-th": "th",
  "ar-sa": "ar",
  "pt-br": "pt",
  "pt-pt": "pt",
  "ru-ru": "ru",
  "uz-uz": "uz",
  "hy-am": "am",
  "it-it": "it",
};

export function normalizeLanguage(input) {
  if (!input) return "zh";
  const raw = String(input).trim().replace(/_/g, "-").toLowerCase();
  if (supportedLocales.includes(raw)) return raw;
  if (localeAliases[raw] && supportedLocales.includes(localeAliases[raw])) {
    return localeAliases[raw];
  }
  const base = raw.split("-")[0];
  if (supportedLocales.includes(base)) return base;
  if (base === "zh") {
    if (raw.includes("tw") || raw.includes("hk") || raw.includes("hant")) {
      return supportedLocales.includes("tw") ? "tw" : "zh";
    }
    return "zh";
  }
  if (base === "hy") return supportedLocales.includes("am") ? "am" : "zh";
  return "zh";
}

// 获取当前语言
export function getLanguage() {
  const chooseLanguage = cookies.get("lang") || localStorage.getItem("locale");
  if (chooseLanguage) return normalizeLanguage(chooseLanguage);

  const language = (
    navigator.language || navigator.browserLanguage
  ).toLowerCase();
  const locales = supportedLocales;
  for (const locale of locales) {
    if (language.indexOf(locale) > -1) {
      return locale;
    }
  }

  if (navigator.userAgent.indexOf("umoocApp") > -1) {
    try {
      let ua = navigator.userAgent;
      if (ua.indexOf("-language-") > -1) {
        let lang = ua.split("-language-")[1].split(" ")[0];
        if (lang) return normalizeLanguage(lang);
      }
    } catch (error) {
      console.log(error);
    }
  }

  return "zh";
}

export const lang = getLanguage();

const tolgeeTools = process.env.VUE_APP_ENABLE_INCONTEXT
  ? InContextTools()
  : DevTools();

const tolgee = Tolgee()
  .use(tolgeeTools)
  .use(FormatSimple())
  .init({
    language: lang,
    apiUrl:
      process.env.VUE_APP_TOLGEE_API_URL || "https://translation.ulearning.app",
    apiKey: process.env.VUE_APP_TOLGEE_API_KEY,
    projectId: 144,
    staticData: messages,
  });

export const i18nReady = tolgee.run().catch((e) => {
  console.warn("[i18n] Tolgee run error:", e);
});

const i18nState = Vue.observable({ locale: lang, tick: 0 });

tolgee.on("language", () => {
  i18nState.locale = tolgee.getLanguage();
});

tolgee.on("update", () => {
  i18nState.tick += 1;
});

class MockVueI18n {
  constructor(options) {
    this._locale = options.locale || "zh";
  }
  get locale() {
    return i18nState.locale;
  }
  set locale(val) {
    tolgee.changeLanguage(val);
  }
  t(key, values) {
    let sanitizedValues = values;
    if (values && typeof values === "object") {
      sanitizedValues = {};
      Object.keys(values).forEach((k) => {
        // Replace undefined/null with empty string to prevent crashes
        // Tolgee might treat empty string as missing, so we use a space or explicit string
        sanitizedValues[k] =
          values[k] === undefined || values[k] === null ? " " : values[k];
      });
    } else {
      sanitizedValues = {};
    }

    // Use a Proxy to provide empty strings for any missing parameters
    // This prevents Tolgee from logging "Missing parameter" errors if translations on the platform are mismatched
    const proxyValues = new Proxy(sanitizedValues, {
      get(target, prop) {
        if (prop === Symbol.iterator) return Reflect.get(target, prop);
        if (prop in target) {
          return target[prop];
        }
        return "";
      },
      has(target, prop) {
        return true; // Always claim we have the parameter
      }
    });

    try {
      return tolgee.t({ key, params: proxyValues, defaultValue: key });
    } catch (e) {
      console.warn(`[i18n] Error translating key "${key}":`, e.message);
      return key;
    }
  }
  tc(key, choice, values) {
    return this.t(key, values);
  }
  te(key) {
    return true;
  }
}

const i18n = new MockVueI18n({ locale: lang });

Vue.mixin({
  computed: {
    $i18n() {
      const _ = i18nState.locale; // trigger reactivity
      const __ = i18nState.tick;
      return i18n;
    },
  },
  methods: {
    $t(key, values) {
      const _ = i18nState.locale; // trigger reactivity
      const __ = i18nState.tick;
      return i18n.t(key, values);
    },
    $tc(key, choice, values) {
      const _ = i18nState.locale; // trigger reactivity
      const __ = i18nState.tick;
      return i18n.tc(key, choice, values);
    },
    $te(key) {
      return i18n.te(key);
    },
  },
});

const getMappedId = (locale, map) => {
  return map[locale] || locale;
};

// 异步加载 UI 组件库语言包
export function loadLanguageAsync(lang) {
  const elementUILang = getMappedId(lang, elementUIMap);
  const vantUILang = getMappedId(lang, vantUIMap);

  if (i18n.locale !== lang) {
    i18n.locale = lang;
  }

  const loadElement = import(
    /* webpackChunkName: "lang-element-[request]" */
    `element-ui/lib/locale/lang/${elementUILang}`
  ).catch(() =>
    import(
      /* webpackChunkName: "lang-element-en" */ "element-ui/lib/locale/lang/en"
    )
  );

  const loadVant = import(
    /* webpackChunkName: "lang-vant-[request]" */
    `vant/lib/locale/lang/${vantUILang}`
  ).catch(() =>
    import(
      /* webpackChunkName: "lang-vant-zh-CN" */ "vant/lib/locale/lang/zh-CN"
    )
  );

  return Promise.all([loadElement, loadVant]).then(([elementMsg, vantMsg]) => {
    elementLocale.use(elementMsg.default);
    if (vantMsg && vantMsg.default) {
      vantLocale.use(vantUILang, vantMsg.default);
    }
    return lang;
  });
}

export default i18n;
